import React from 'react'
import { observer } from 'mobx-react-lite'

import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import ListItemButton from '@mui/material/ListItemButton'
import Stack from '@mui/material/Stack'

import { ChevronRight } from '@mxenabled/mx-icons'

import { H3 } from '@mxenabled/mxui'

import {
  Drawer,
  Loader,
  useAccountStore,
  useAppConfig,
  useGlobalCopyStore,
  useGlobalUiStore,
  useInsightsEnabled,
  useSettingsStore,
  useWidgetLoadTimer,
} from '../../../common'
import { NotificationTypes } from '../../../core'

import { AccountNotificationProfiles } from './AccountNotificationProfiles'
import { BudgetNotificationProfiles } from './BudgetNotificationProfiles'
import { DeliveryMethods } from './DeliveryMethods'
import { InsightsNotificationProfiles } from './InsightsNotificationProfiles'
import PersonalDetails from './PersonalDetails'

export interface NotificationSettingsProps {
  notificationTypes?: NotificationTypes[]
  showPersonalDetails?: boolean
  view?: string | undefined
}

export const NotificationSettings: React.FC<NotificationSettingsProps> = observer(
  ({
    notificationTypes = [NotificationTypes.Accounts, NotificationTypes.Budgets],
    showPersonalDetails = false,
    view,
  }) => {
    const { config } = useAppConfig()
    const { settings: copy } = useGlobalCopyStore()
    const { isInitialized } = useGlobalUiStore()
    const { isAccountDataLoaded, loadAccountData } = useAccountStore()
    const { clientCommunicationProfile: ccp, loadNotificationProfiles } = useSettingsStore()
    const isInsightsEnabled = useInsightsEnabled()

    const [isLoaded, setIsLoaded] = React.useState(false)
    const [profileTypeOpen, setProfileTypeOpen] = React.useState<NotificationTypes | undefined>(
      undefined,
    )

    if (notificationTypes.length > 0 && isInsightsEnabled)
      notificationTypes.push(NotificationTypes.Insights)

    useWidgetLoadTimer({
      widgetName: 'NotificationSettingsWidget',
      isLoaded: isLoaded,
    })

    React.useEffect(() => {
      if (!isAccountDataLoaded) {
        loadAccountData().finally()
      }
    }, [])

    React.useEffect(() => {
      if (isInitialized) {
        loadNotificationProfiles().finally(() => setIsLoaded(true))
      }
    }, [isInitialized])

    if (!isInitialized) {
      return <Loader />
    }

    let drawerTitle = copy.manage_account_notifications
    if (profileTypeOpen === NotificationTypes.Budgets) {
      drawerTitle = copy.manage_budget_notifications
    } else if (profileTypeOpen === NotificationTypes.Insights) {
      drawerTitle = copy.manage_insights_notifications
    }

    const {
      email_notifications_enabled: emailEnabled,
      sms_notifications_enabled: smsEnabled,
      push_notifications_enabled: pushEnabled,
    } = ccp

    const hasDeliveryMethods = emailEnabled || smsEnabled || pushEnabled

    if (view === 'accounts') {
      return <AccountNotificationProfiles />
    } else if (view === 'budgets') {
      return <BudgetNotificationProfiles />
    } else if (view === 'insights') {
      return <InsightsNotificationProfiles />
    }

    const canEditFields =
      config.display_email_edit_field_in_settings || config.display_sms_edit_field_in_settings
    const shouldShowPersonalDetails =
      showPersonalDetails && ccp.can_edit_contact_info && canEditFields

    return (
      <Stack sx={{ pt: hasDeliveryMethods ? 0 : 24 }}>
        <Stack sx={{ px: 24 }}>
          {hasDeliveryMethods && (
            <>
              <DeliveryMethods />
              <Divider sx={{ my: 24 }} />
            </>
          )}

          <H3 sx={{ mb: 4 }}>{copy.notification_types_title}</H3>
        </Stack>
        <List>
          {notificationTypes.includes(NotificationTypes.Accounts) && (
            <ListItem>
              <ListItemButton onClick={() => setProfileTypeOpen(NotificationTypes.Accounts)}>
                <ListItemText primary={copy.accounts_title} secondary={copy.accounts_subtitle} />
                <ChevronRight />
              </ListItemButton>
            </ListItem>
          )}
          {notificationTypes.includes(NotificationTypes.Budgets) && (
            <ListItem>
              <ListItemButton onClick={() => setProfileTypeOpen(NotificationTypes.Budgets)}>
                <ListItemText primary={copy.budgets_title} secondary={copy.budgets_subtitle} />
                <ChevronRight />
              </ListItemButton>
            </ListItem>
          )}
          {notificationTypes.includes(NotificationTypes.Insights) && (
            <ListItem>
              <ListItemButton onClick={() => setProfileTypeOpen(NotificationTypes.Insights)}>
                <ListItemText primary={copy.insights_title} secondary={copy.insights_subtitle} />
                <ChevronRight />
              </ListItemButton>
            </ListItem>
          )}
        </List>

        {shouldShowPersonalDetails && (
          <Stack sx={{ px: 24 }}>
            <PersonalDetails />
          </Stack>
        )}

        <Drawer
          isOpen={Boolean(profileTypeOpen)}
          onClose={() => setProfileTypeOpen(undefined)}
          title={drawerTitle}
        >
          <Stack>
            {profileTypeOpen === NotificationTypes.Accounts && <AccountNotificationProfiles />}
            {profileTypeOpen === NotificationTypes.Budgets && <BudgetNotificationProfiles />}
            {profileTypeOpen === NotificationTypes.Insights && <InsightsNotificationProfiles />}
          </Stack>
        </Drawer>
      </Stack>
    )
  },
)
